// @ts-ignore
// eslint-disable-next-line

import { useEffect, useRef, useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PrimaryBtnBlue } from './Buttons'
import Styles from './Pip.module.css'
import { Tip } from './Tip'
import { VideoMessage } from '../apis/api-egg'
import { useUI } from '../context/UIContext'

// Feature flag to force using Pip for YouTube videos even when cross-origin isolated
const FORCE_YOUTUBE_PIP = process.env.REACT_APP_FORCE_YOUTUBE_PIP === 'true'

export const Pip = () => {
    const { pip, closePip } = useUI()
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const iframeRef = useRef<HTMLIFrameElement | null>(null)
    const [isYouTubeEmbed, setIsYouTubeEmbed] = useState(false)

    useEffect(() => {
        // Check if the source is a YouTube embed URL
        if (pip?.src && typeof pip.src === 'string') {
            setIsYouTubeEmbed(pip.src.includes('youtube.com/embed/'))
        }

        const video = videoRef.current
        if (!video || isYouTubeEmbed) return

        video.addEventListener('ended', closePip)

        return () => video.removeEventListener('ended', closePip)
    }, [closePip, pip?.src, isYouTubeEmbed])

    if (!pip?.src) return null

    const { mute, onMute, onUnmute, ...rest } = pip

    const goPip = () => {
        if (isYouTubeEmbed) {
            // YouTube videos in iframes can't use PiP API directly
            // We could open in a new window instead
            window.open(pip.src as string, '_blank', 'noopener,noreferrer')
            return
        }

        if (document.pictureInPictureElement) document.exitPictureInPicture()
        else if (document.pictureInPictureEnabled) videoRef.current?.requestPictureInPicture()
    }

    return (
        <section className={Styles.pip}>
            <div className={Styles.pipContent}>
                {isYouTubeEmbed ? (
                    <iframe
                        ref={iframeRef}
                        src={`${pip.src as string}${mute ? '?mute=1' : ''}`}
                        width={800}
                        height={450}
                        frameBorder="0"
                        // @ts-ignore
                        credentialless="true"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                ) : (
                    <video ref={videoRef} autoPlay controls width={800} {...rest} />
                )}

                <span className={`${Styles.audioNotice} ${mute ? '' : Styles.enabled}`}>
                    Sonido de participantes {mute ? ' deshabilitado' : ' habilitado'}
                </span>

                <div className={Styles.controls}>
                    <PrimaryBtnBlue data-tooltip-id="pip-close" className="primary" onClick={closePip}>
                        <FontAwesomeIcon icon={['fas', 'times']} />
                    </PrimaryBtnBlue>
                    <Tip id="pip-close" content="Cerrar video" place={'left'} />

                    <PrimaryBtnBlue data-tooltip-id="pip-pip" className="primary" onClick={goPip}>
                        <FontAwesomeIcon icon={['fas', 'arrow-up-right-from-square']} />
                    </PrimaryBtnBlue>
                    <Tip id="pip-pip" content="Modo Picture-in-Picture" place={'left'} />

                    {onMute && onUnmute && !isYouTubeEmbed && (
                        <>
                            <PrimaryBtnBlue
                                data-tooltip-id="pip-enable-audio"
                                className="primary"
                                onClick={mute ? onUnmute : onMute}
                            >
                                <FontAwesomeIcon
                                    icon={['far', mute ? 'volume-slash' : 'volume']}
                                    color={mute ? 'rgba(255, 100, 124, 0.8)' : 'white'}
                                />
                            </PrimaryBtnBlue>
                            <Tip
                                id="pip-enable-audio"
                                content={`${mute ? 'Habilitar' : 'Deshabilitar'} el sonido de los participantes`}
                                place={'left'}
                            />
                        </>
                    )}
                </div>
            </div>
        </section>
    )
}

export type PipType = Omit<VideoMessage, 'type'> & {
    onClose?: () => unknown
    onMute?: () => void
    onUnmute?: () => void
}
